import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import LoginPage from "./LoginPage";
import { useAuth } from "./useAuth";
import { RoleEnum, hasRole } from "./constants/roles";
import {
  canAccessAdminModules as canManageAdminModules,
  canAccessAdminUsersStrict as canManageAdminUsers,
  canEditGovernance as canWriteGovernanceModule,
  canViewActions as canReadActionsModule,
  canViewAudit as canReadAuditModule,
  canViewControls as canReadControlsModule,
  canViewGovernance as canReadGovernanceModule,
  canViewIncidents as canReadIncidentsModule,
  canViewKpis as canReadKpisModule,
  canViewPolicies as canReadPoliciesModule,
  canViewRisks as canReadRisksModule,
  canViewSupportingEvidence as canReadSupportingEvidenceModule,
} from "./accessControl";
import FrameworkList from "./pages/frameworks/FrameworkList";
import FrameworkDetail from "./pages/frameworks/FrameworkDetail";
import FrameworkForm from "./pages/frameworks/FrameworkForm";
import RequirementForm from "./pages/requirements/RequirementForm";
import RequirementDetail from "./pages/requirements/RequirementDetail";
import ControlForm from "./pages/controls/ControlForm";
import ControlDetail from "./pages/controls/ControlDetail";
import ControlRequirementMapping from "./pages/mappings/ControlRequirementMapping";
import BoardBacksList from "./pages/boardbacks/BoardBacksList";
import BoardBackEditor from "./pages/boardbacks/BoardBackEditor";
import MeetingMode from "./pages/boardbacks/MeetingMode";
import AuditUniverseDetail from "./pages/audit/AuditUniverseDetail";
import AuditPlans from "./pages/audit/AuditPlans";
import AuditModule from "./pages/audit/AuditModule";
import KpiList from "./pages/kpis/KpiList";
import KpiDetail from "./pages/kpis/KpiDetail";
import KpiDashboard from "./pages/kpis/KpiDashboard";
import RiskDetailsPage from "./pages/risks/RiskDetailsPage";

export const canAccessExecutiveKpis = (user) => {
  return canReadKpisModule(user) && hasRole(user?.role, [RoleEnum.ADMIN, RoleEnum.EXEC, RoleEnum.BOARD]);
};

export const canAccessExecutiveRiskDashboard = (user) => {
  return canReadRisksModule(user);
};

export const canAccessAdminUsers = (user) => {
  return canManageAdminUsers(user);
};

export const canAccessAdminModules = (user) => {
  return canManageAdminModules(user);
};

export const canAccessAuditModule = (user) => {
  return canReadAuditModule(user);
};

export const canAccessActionsModule = (user) => {
  return canReadActionsModule(user);
};

export const canAccessIncidentsModule = (user) => {
  return canReadIncidentsModule(user);
};

export const canAccessControlsModule = (user) => {
  return canReadControlsModule(user);
};

export const canAccessRisksModule = (user) => {
  return canReadRisksModule(user);
};

export const canAccessPoliciesModule = (user) => {
  return canReadPoliciesModule(user);
};

export const canAccessKpisModule = (user) => {
  return canReadKpisModule(user);
};

export const canAccessGovernanceModule = (user) => {
  return canReadGovernanceModule(user);
};

export const canEditGovernanceModule = (user) => {
  return canWriteGovernanceModule(user);
};

export const canAccessSupportingEvidenceModule = (user) => {
  return canReadSupportingEvidenceModule(user);
};

function LegacyAuditDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/audit/audits/${id}`} replace />;
}

function AccessDeniedState({ title, message }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 32,
        background: "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)",
      }}
    >
      <div
        style={{
          width: "min(560px, 100%)",
          background: "rgba(255,255,255,0.96)",
          border: "1px solid rgba(15, 23, 42, 0.08)",
          borderRadius: 18,
          padding: 28,
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.12)",
        }}
      >
        <div style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: "#0f766e" }}>
          Access restricted
        </div>
        <h2 style={{ margin: "12px 0 8px", color: "#0f172a" }}>{title}</h2>
        <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>{message}</p>
      </div>
    </div>
  );
}

export default function AppRoutes() {
  const { token, user, accessReady = true } = useAuth();
  const execAccess = canAccessExecutiveKpis(user);
  const adminUserAccess = canAccessAdminUsers(user);
  const adminModuleAccess = canAccessAdminModules(user);
  const auditAccess = canAccessAuditModule(user);
  const actionsAccess = canAccessActionsModule(user);
  const controlsAccess = canAccessControlsModule(user);
  const incidentsAccess = canAccessIncidentsModule(user);
  const policiesAccess = canAccessPoliciesModule(user);
  const risksAccess = canAccessRisksModule(user);
  const kpisAccess = canAccessKpisModule(user);
  const governanceAccess = canAccessGovernanceModule(user);
  const governanceEditAccess = canEditGovernanceModule(user);
  const supportingEvidenceAccess = canAccessSupportingEvidenceModule(user);

  const renderAuditRoute = (element) =>
    token ? (
      !accessReady ? (
        <AccessDeniedState
          title="Loading Audit access"
          message="Glyder is verifying your current Audit module access."
        />
      ) : auditAccess ? (
        element
      ) : (
        <AccessDeniedState
          title="Audit access denied"
          message="You need at least View access to the Audit module to open this area."
        />
      )
    ) : (
      <Navigate to="/login" replace />
    );

  const renderIncidentsRoute = (element) =>
    token ? (
      !accessReady ? (
        <AccessDeniedState
          title="Loading Incident access"
          message="Glyder is verifying your current Incidents module access."
        />
      ) : incidentsAccess ? (
        element
      ) : (
        <AccessDeniedState
          title="Incidents access denied"
          message="You need at least View access to the Incidents module to open this area."
        />
      )
    ) : (
      <Navigate to="/login" replace />
    );

  const renderActionsRoute = (element) =>
    token ? (
      !accessReady ? (
        <AccessDeniedState
          title="Loading Action access"
          message="Glyder is verifying your current Actions module access."
        />
      ) : actionsAccess ? (
        element
      ) : (
        <AccessDeniedState
          title="Actions access denied"
          message="You need at least View access to the Actions module to open this area."
        />
      )
    ) : (
      <Navigate to="/login" replace />
    );

  const renderControlsRoute = (element) =>
    token ? (
      !accessReady ? (
        <AccessDeniedState
          title="Loading Control access"
          message="Glyder is verifying your current Controls module access."
        />
      ) : controlsAccess ? (
        element
      ) : (
        <AccessDeniedState
          title="Controls access denied"
          message="You need at least View access to the Controls module to open this area."
        />
      )
    ) : (
      <Navigate to="/login" replace />
    );

  const renderAdminModulesRoute = (element) =>
    token ? (
      !accessReady ? (
        <AccessDeniedState
          title="Loading Admin Module Portal access"
          message="Glyder is verifying your current Admin Module Portal access."
        />
      ) : adminModuleAccess ? (
        element
      ) : (
        <AccessDeniedState
          title="Admin Module Portal access denied"
          message="You need Manage access to Admin Module Portal to open this area."
        />
      )
    ) : (
      <Navigate to="/login" replace />
    );

  const renderRisksRoute = (element) =>
    token ? (
      !accessReady ? (
        <AccessDeniedState
          title="Loading Risk access"
          message="Glyder is verifying your current Risks module access."
        />
      ) : risksAccess ? (
        element
      ) : (
        <AccessDeniedState
          title="Risks access denied"
          message="You need at least View access to the Risks module to open this area."
        />
      )
    ) : (
      <Navigate to="/login" replace />
    );

  const renderPoliciesRoute = (element) =>
    token ? (
      !accessReady ? (
        <AccessDeniedState
          title="Loading Policy access"
          message="Glyder is verifying your current Policies module access."
        />
      ) : policiesAccess ? (
        element
      ) : (
        <AccessDeniedState
          title="Policies access denied"
          message="You need at least View access to the Policies module to open this area."
        />
      )
    ) : (
      <Navigate to="/login" replace />
    );

  const renderKpisRoute = (element) =>
    token ? (
      !accessReady ? (
        <AccessDeniedState
          title="Loading KPI access"
          message="Glyder is verifying your current KPIs module access."
        />
      ) : kpisAccess ? (
        element
      ) : (
        <AccessDeniedState
          title="KPIs access denied"
          message="You need at least View access to the KPIs module to open this area."
        />
      )
    ) : (
      <Navigate to="/login" replace />
    );

  const renderGovernanceRoute = (element) =>
    token ? (
      !accessReady ? (
        <AccessDeniedState
          title="Loading Board Packs and Documents access"
          message="Glyder is verifying your current Board Packs and Documents workspace access."
        />
      ) : governanceAccess ? (
        element
      ) : (
        <AccessDeniedState
          title="Board Packs and Documents access denied"
          message="You need at least View access to Board Packs and Documents to open this area."
        />
      )
    ) : (
      <Navigate to="/login" replace />
    );

  const renderGovernanceEditRoute = (element) =>
    token ? (
      !accessReady ? (
        <AccessDeniedState
          title="Loading Board Packs and Documents access"
          message="Glyder is verifying your current Board Packs and Documents workspace access."
        />
      ) : governanceEditAccess ? (
        element
      ) : (
        <AccessDeniedState
          title="Board Pack create access denied"
          message="You need at least Edit access to create a board pack."
        />
      )
    ) : (
      <Navigate to="/login" replace />
    );

  const renderSupportingEvidenceRoute = (element) =>
    token ? (
      !accessReady ? (
        <AccessDeniedState
          title="Loading Supporting Evidence access"
          message="Glyder is verifying your current supporting evidence workspace access."
        />
      ) : supportingEvidenceAccess ? (
        element
      ) : (
        <AccessDeniedState
          title="Supporting Evidence access denied"
          message="You need at least View access to a Glyder module that contributes supporting evidence to open this area."
        />
      )
    ) : (
      <Navigate to="/login" replace />
    );

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          path="/"
          element={
            token ? <DashboardLayout /> : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/executive/kpis"
          element={
            token ? (
              !accessReady ? (
                <AccessDeniedState
                  title="Loading Executive KPI access"
                  message="Glyder is verifying your current KPI module and executive dashboard access."
                />
              ) : execAccess ? (
                <DashboardLayout initialModule="executive-kpis" />
              ) : (
                <AccessDeniedState
                  title="Executive KPI access denied"
                  message="You need at least View access to KPIs and the existing executive role access to open this dashboard."
                />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/executive/risk-dashboard"
          element={
            renderRisksRoute(<DashboardLayout initialModule="executive-risk-dashboard" />)
          }
        />
        <Route
          path="/admin/users"
          element={
            token ? (
              !accessReady ? (
                <AccessDeniedState
                  title="Loading Admin User Portal access"
                  message="Glyder is verifying your current Admin User Portal access."
                />
              ) : adminUserAccess ? (
                <DashboardLayout initialModule="admin-users" />
              ) : (
                <AccessDeniedState
                  title="Admin User Portal access denied"
                  message="You need Manage access to Admin User Portal to open this area."
                />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/admin/risk-categories"
          element={
            token ? (
              !accessReady ? (
                <AccessDeniedState
                  title="Loading Admin Module Portal access"
                  message="Glyder is verifying your current Admin Module Portal access."
                />
              ) : adminModuleAccess ? (
                <DashboardLayout initialModule="admin-risk-categories" />
              ) : (
                <AccessDeniedState
                  title="Admin Module Portal access denied"
                  message="You need Manage access to Admin Module Portal to open this area."
                />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/admin/control-settings"
          element={
            token ? (
              !accessReady ? (
                <AccessDeniedState
                  title="Loading Admin Module Portal access"
                  message="Glyder is verifying your current Admin Module Portal access."
                />
              ) : adminModuleAccess ? (
                <DashboardLayout initialModule="admin-control-settings" />
              ) : (
                <AccessDeniedState
                  title="Admin Module Portal access denied"
                  message="You need Manage access to Admin Module Portal to open this area."
                />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="/admin/control-categories" element={<Navigate to="/admin/control-settings" replace />} />
        <Route
          path="/admin/risk-appetite"
          element={
            token ? (
              !accessReady ? (
                <AccessDeniedState
                  title="Loading Admin Module Portal access"
                  message="Glyder is verifying your current Admin Module Portal access."
                />
              ) : adminModuleAccess ? (
                <DashboardLayout initialModule="admin-risk-appetite" />
              ) : (
                <AccessDeniedState
                  title="Admin Module Portal access denied"
                  message="You need Manage access to Admin Module Portal to open this area."
                />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/admin/risk-matrix"
          element={
            token ? (
              !accessReady ? (
                <AccessDeniedState
                  title="Loading Admin Module Portal access"
                  message="Glyder is verifying your current Admin Module Portal access."
                />
              ) : adminModuleAccess ? (
                <DashboardLayout initialModule="admin-risk-matrix" />
              ) : (
                <AccessDeniedState
                  title="Admin Module Portal access denied"
                  message="You need Manage access to Admin Module Portal to open this area."
                />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/admin/actions"
          element={
            token ? (
              !accessReady ? (
                <AccessDeniedState
                  title="Loading Admin Module Portal access"
                  message="Glyder is verifying your current Admin Module Portal access."
                />
              ) : adminModuleAccess ? (
                <DashboardLayout initialModule="admin-actions" />
              ) : (
                <AccessDeniedState
                  title="Admin Module Portal access denied"
                  message="You need Manage access to Admin Module Portal to open this area."
                />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/admin/incidents"
          element={
            token ? (
              !accessReady ? (
                <AccessDeniedState
                  title="Loading Admin Module Portal access"
                  message="Glyder is verifying your current Admin Module Portal access."
                />
              ) : adminModuleAccess ? (
                <DashboardLayout initialModule="admin-incidents" />
              ) : (
                <AccessDeniedState
                  title="Admin Module Portal access denied"
                  message="You need Manage access to Admin Module Portal to open this area."
                />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/admin/notifications"
          element={
            token ? (
              !accessReady ? (
                <AccessDeniedState
                  title="Loading Notification Administration access"
                  message="Glyder is verifying your current Admin Module Portal access."
                />
              ) : adminModuleAccess ? (
                <DashboardLayout initialModule="admin-notifications" />
              ) : (
                <AccessDeniedState
                  title="Notification Administration access denied"
                  message="You need Manage access to Admin Module Portal to open this area."
                />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/actions"
          element={renderActionsRoute(<DashboardLayout initialModule="actions" />)}
        />
        <Route
          path="/actions/:actionId"
          element={renderActionsRoute(<DashboardLayout initialModule="actions" />)}
        />
        <Route
          path="/incidents"
          element={renderIncidentsRoute(<DashboardLayout initialModule="incidents" />)}
        />
        <Route
          path="/incidents/:incidentId"
          element={renderIncidentsRoute(<DashboardLayout initialModule="incidents" />)}
        />
        <Route
          path="/catalog/assets"
          element={renderAdminModulesRoute(<DashboardLayout initialModule="assets" />)}
        />
        <Route
          path="/catalog/vendors"
          element={renderAdminModulesRoute(<DashboardLayout initialModule="vendors" />)}
        />
        <Route
          path="/catalog/policies"
          element={renderPoliciesRoute(<DashboardLayout initialModule="policies" />)}
        />
        <Route
          path="/catalog/policies/:tenantPolicyId"
          element={renderPoliciesRoute(<DashboardLayout initialModule="policies" />)}
        />
        <Route
          path="/documents/supporting-evidence"
          element={renderSupportingEvidenceRoute(<DashboardLayout initialModule="supporting-evidence" />)}
        />
        <Route
          path="/controls/:cid"
          element={renderControlsRoute(
              <DashboardLayout initialModule="controls">
                <ControlDetail />
              </DashboardLayout>
            )}
        />
        <Route
          path="/risks"
          element={renderRisksRoute(<DashboardLayout initialModule="risks" />)}
        />
        <Route
          path="/risks/:riskId"
          element={renderRisksRoute(
            <DashboardLayout initialModule="risks">
              <RiskDetailsPage />
            </DashboardLayout>
          )}
        />

        <Route
          path="/frameworks"
          element={renderAdminModulesRoute(<FrameworkList />)}
        />
        <Route
          path="/frameworks/new"
          element={renderAdminModulesRoute(<FrameworkForm />)}
        />
        <Route
          path="/frameworks/:id"
          element={renderAdminModulesRoute(<FrameworkDetail />)}
        />
        <Route
          path="/frameworks/:id/edit"
          element={renderAdminModulesRoute(<FrameworkForm />)}
        />
        <Route
          path="/frameworks/:fid/requirements/new"
          element={renderAdminModulesRoute(<RequirementForm />)}
        />
        <Route
          path="/frameworks/:fid/requirements/:rid/edit"
          element={renderAdminModulesRoute(<RequirementForm />)}
        />
        <Route
          path="/requirements/:rid"
          element={renderAdminModulesRoute(<RequirementDetail />)}
        />
        <Route
          path="/controls"
          element={renderControlsRoute(<DashboardLayout initialModule="controls" />)}
        />
        <Route
          path="/controls/new"
          element={renderControlsRoute(
              <DashboardLayout initialModule="controls">
                <ControlForm />
              </DashboardLayout>
            )}
        />
        <Route
          path="/controls/:cid/edit"
          element={renderControlsRoute(
              <DashboardLayout initialModule="controls">
                <ControlForm />
              </DashboardLayout>
            )}
        />
        <Route
          path="/requirements/:rid/map"
          element={renderControlsRoute(<ControlRequirementMapping />)}
        />
        <Route path="/boardbacks" element={renderGovernanceRoute(<BoardBacksList />)} />
        <Route path="/boardbacks/new" element={renderGovernanceEditRoute(<BoardBacksList initialCreateOpen />)} />
        <Route path="/boardbacks/:id/edit" element={renderGovernanceEditRoute(<BoardBackEditor />)} />
        <Route path="/boardbacks/:id/meeting" element={renderGovernanceRoute(<MeetingMode />)} />
        <Route path="/audit" element={renderAuditRoute(<Navigate to="/audit/audits" replace />)} />
        <Route
          path="/audit/audits"
          element={renderAuditRoute(
            <DashboardLayout initialModule="audit">
              <AuditModule />
            </DashboardLayout>
          )}
        />
        <Route
          path="/audit/audits/:id"
          element={renderAuditRoute(
            <DashboardLayout initialModule="audit">
              <AuditModule />
            </DashboardLayout>
          )}
        />
        <Route
          path="/audit/planning"
          element={renderAuditRoute(
            <DashboardLayout initialModule="audit">
              <AuditModule />
            </DashboardLayout>
          )}
        />
        <Route path="/auditable-entities" element={renderAuditRoute(<Navigate to="/audit/planning?view=entities" replace />)} />
        <Route path="/auditable-entities/:id" element={renderAuditRoute(<AuditUniverseDetail />)} />
        <Route path="/audit-plans" element={renderAuditRoute(<Navigate to="/audit/planning" replace />)} />
        <Route path="/audit-plans/:id" element={renderAuditRoute(<AuditPlans />)} />
        <Route path="/audit-engagements" element={renderAuditRoute(<Navigate to="/audit/audits" replace />)} />
        <Route path="/audit-engagements/:id" element={renderAuditRoute(<LegacyAuditDetailRedirect />)} />
        <Route path="/audit-universe" element={renderAuditRoute(<Navigate to="/audit/planning?view=entities" replace />)} />
        <Route path="/audit-universe/:id" element={renderAuditRoute(<AuditUniverseDetail />)} />
        <Route path="/audits" element={renderAuditRoute(<Navigate to="/audit/audits" replace />)} />
        <Route path="/audits/:id" element={renderAuditRoute(<LegacyAuditDetailRedirect />)} />
        <Route path="/kpis" element={renderKpisRoute(<KpiList />)} />
        <Route path="/kpis/new" element={renderKpisRoute(<KpiDetail />)} />
        <Route path="/kpis/:id" element={renderKpisRoute(<KpiDetail />)} />
        <Route path="/kpi-dashboards" element={renderKpisRoute(<KpiDashboard />)} />
      </Routes>
    </BrowserRouter>
  );
}
